import type { BreakRulesConfig, HoursRateConfig, NurseRole, OvertimeConfig } from "../models";
import { EmploymentType } from "../models";

/*
 * API-shape → form-shape conversions for the three wage rule tables (hours rate, overtime, break
 * rules). Shared by Settings → Department Configuration (initial load of the create form) and the
 * Team page's Wage Settings tab, which owns editing these rules since DAT-905.
 */

/**
 * The API stores "this rule applies to everything" as the empty array — see the `[] = all` notes on
 * `BreakRulesConfig` / `OvertimeConfig` in `utils/models.ts`, written by the
 * `_normalize_attr_if_equals_full_set` calls in `backend/database/departments.py`. The Rates-tab
 * tables instead hold an explicit full selection and read `[]` as "nothing chosen yet", which shows
 * a red caption and disables Save.
 *
 * APP-450: these are exported because BOTH entry points into form state must apply them. The initial
 * load did; the post-save server echo did not, so a successful save wrote the canonical `[]` back
 * into the form and immediately re-disabled Save. The department config read as unsavable while in
 * fact every attempt had already persisted.
 */
const expandAllMarker = <T>(stored: readonly T[] | null | undefined, fullSet: readonly T[]): T[] => (stored?.length ? [...stored] : [...fullSet]);

/**
 * No `expandAllMarker` here, unlike roles/employment types: the table reads the canonical markers
 * directly, so a pre-union row carrying neither must decode to exactly that no-op pair — otherwise an
 * existing config stops producing identical hours.
 */
const unionScopeFromApi = (config: { union?: string[] | null; isUnion?: boolean | null }): { union: string[]; isUnion: boolean | null } => ({
	union: Array.isArray(config.union) && config.union.length > 0 ? [...config.union] : [],
	isUnion: config.isUnion ?? null,
});

export const breakRulesConfigFromApi = (configs: BreakRulesConfig[] | null | undefined, deptRoles: NurseRole[]): BreakRulesConfig[] =>
	(configs ?? []).map((config) => ({
		...config,
		roles: expandAllMarker(config.roles, deptRoles),
		employmentTypes: expandAllMarker(config.employmentTypes, Object.values(EmploymentType)),
		...unionScopeFromApi(config),
	}));

export const overtimeConfigFromApi = (configs: OvertimeConfig[] | null | undefined, deptRoles: NurseRole[]): OvertimeConfig[] =>
	(configs ?? []).map((config) => {
		// Rows written before the column became an array carry a bare string.
		const empTypes = Array.isArray(config.employmentType)
			? config.employmentType
			: (() => {
					const legacy = (config as unknown as { employmentType?: string | null }).employmentType;
					return legacy ? [legacy] : [];
				})();
		return {
			...config,
			roles: expandAllMarker(config.roles, deptRoles),
			employmentType: expandAllMarker(empTypes, Object.values(EmploymentType)),
			...unionScopeFromApi(config),
		};
	});

/**
 * `trainingStatuses` / `employmentTypes` are deliberately NOT expanded: the hours-rate cells read the
 * canonical `[]` as "all" themselves, and only `undefined` means "not chosen".
 */
export const hoursRateConfigFromApi = (configs: HoursRateConfig[] | null | undefined, deptRoles: NurseRole[]): HoursRateConfig[] =>
	(configs ?? []).map((config) => ({
		...config,
		roles: expandAllMarker(config.roles, deptRoles),
		// The server echoes time columns as "HH:MM:SS"; the field round-trips "HH:MM".
		startTime: config.startTime ? config.startTime.split(":").slice(0, 2).join(":") : config.startTime,
		endTime: config.endTime ? config.endTime.split(":").slice(0, 2).join(":") : config.endTime,
		// DEFAULT: [] is the column's own "all agencies" marker (server_default '{}'), so decoding a
		// null/absent value to it matches what the backend would have stored. Kept because the agency
		// cell dereferences `.length` and would otherwise crash the whole Rates tab on a null.
		agency: config.agency ?? [],
		...unionScopeFromApi(config),
	}));
